import { BookOpen, FlaskConical, Stethoscope, type LucideIcon } from 'lucide-react'

export type StudyStatus = 'draft' | 'in_review' | 'published' | 'exported'
export type VersionStatus = 'draft' | 'validated' | 'published'

export interface StudyDesignVersion {
  id: string
  versionNumber: string
  status: VersionStatus
  odmFileOid?: string
  xmlBlobPath?: string
  createdAt: string
  createdBy: string
  changeSummary?: string
  checksumShort?: string
}

export interface StudyDesignStudy {
  id: string
  slug: string
  code: string
  title: string
  phase: string
  status: StudyStatus
  currentVersionId: string
  versions: StudyDesignVersion[]
  forms: number
  items: number
  lastActiveAt: string
  stepIdx: number
  Icon: LucideIcon
  /** Bound chat session once the user opens this study in the workspace. */
  sessionId?: string
  /** True once the study has been persisted to the data-service (opt-in). */
  cloud?: boolean
}

export const DESIGN_STEPS = [
  'Upload',
  'Study shape',
  'Endpoints',
  'Schedule',
  'Timeline',
  'Procedures',
  'Forms',
  'Protocol',
] as const

export const MOCK_STUDIES: StudyDesignStudy[] = [
  {
    id: 'vector-ep',
    slug: 'vector-ep',
    code: 'VECTOR-EP',
    title: 'Ventricular Electrophysiology Cohort for Therapy Outcomes Research',
    phase: 'Phase III',
    status: 'draft',
    currentVersionId: 'vector-v1-2',
    stepIdx: 2,
    forms: 12,
    items: 847,
    lastActiveAt: '2 hours ago',
    Icon: Stethoscope,
    versions: [
      {
        id: 'vector-v1-0',
        versionNumber: '1.0',
        status: 'published',
        createdAt: '2026-05-12T10:00:00Z',
        createdBy: 'Dr. Chen',
        changeSummary: 'Initial protocol skeleton',
        checksumShort: 'a3f8c2',
      },
      {
        id: 'vector-v1-1',
        versionNumber: '1.1',
        status: 'validated',
        createdAt: '2026-05-28T14:30:00Z',
        createdBy: 'Dr. Chen',
        changeSummary: 'Added endpoint definitions',
        checksumShort: 'b7e1d4',
      },
      {
        id: 'vector-v1-2',
        versionNumber: '1.2',
        status: 'draft',
        createdAt: '2026-06-29T09:15:00Z',
        createdBy: 'Dr. Chen',
        changeSummary: 'CDASH mapping for Demographics & Medical History',
        checksumShort: 'c9a2f1',
      },
    ],
  },
  {
    id: 'beacon-vt',
    slug: 'beacon-vt',
    code: 'BEACON-VT',
    title: 'Baseline Evaluation of Ablation & Catheter Outcomes Network for Ventricular Tachycardia',
    phase: 'Post-market · IV',
    status: 'in_review',
    currentVersionId: 'beacon-v2-0',
    stepIdx: 6,
    forms: 17,
    items: 312,
    lastActiveAt: '3 days ago',
    Icon: FlaskConical,
    versions: [
      {
        id: 'beacon-v1-0',
        versionNumber: '1.0',
        status: 'published',
        createdAt: '2026-04-01T08:00:00Z',
        createdBy: 'S. Patel',
        changeSummary: 'Registry baseline',
        checksumShort: 'd4b8e7',
      },
      {
        id: 'beacon-v2-0',
        versionNumber: '2.0',
        status: 'validated',
        createdAt: '2026-06-28T16:45:00Z',
        createdBy: 'S. Patel',
        changeSummary: 'CRF inventory complete — ready for Norma handoff',
        checksumShort: 'e2c5a9',
      },
    ],
  },
  {
    id: 'pulsar-af',
    slug: 'pulsar-af',
    code: 'PULSAR-AF',
    title: 'Pulsed-field Ablation Utility & Long-term Sinus-rhythm Assessment in Atrial Fibrillation',
    phase: 'Phase II',
    status: 'exported',
    currentVersionId: 'pulsar-v1-3',
    stepIdx: 8,
    forms: 14,
    items: 603,
    lastActiveAt: '1 week ago',
    Icon: BookOpen,
    versions: [
      {
        id: 'pulsar-v1-0',
        versionNumber: '1.0',
        status: 'published',
        createdAt: '2026-02-10T11:00:00Z',
        createdBy: 'Dr. Okonkwo',
        changeSummary: 'Protocol draft',
        checksumShort: 'f1a3b6',
        xmlBlobPath: 'files/pulsar-af-v1-0.xml',
      },
      {
        id: 'pulsar-v1-2',
        versionNumber: '1.2',
        status: 'published',
        createdAt: '2026-05-15T13:20:00Z',
        createdBy: 'Dr. Okonkwo',
        changeSummary: 'Final protocol lock',
        checksumShort: 'g8d2c4',
        xmlBlobPath: 'files/pulsar-af-v1-2.xml',
      },
      {
        id: 'pulsar-v1-3',
        versionNumber: '1.3',
        status: 'published',
        createdAt: '2026-06-22T10:00:00Z',
        createdBy: 'Dr. Okonkwo',
        changeSummary: 'ODM 1.3.1 export for Anvil',
        checksumShort: 'h5e7f9',
        xmlBlobPath: 'files/pulsar-af-v1-3.xml',
        odmFileOid: 'ODM.PULSAR-AF.001',
      },
    ],
  },
]

import { getUserStudyBySlug, listUserStudies } from '@/lib/user-study-registry'

export function listStudies(userId?: string | null): StudyDesignStudy[] {
  return [...listUserStudies(userId), ...MOCK_STUDIES]
}

export function getStudyBySlug(
  slug: string | null | undefined,
  userId?: string | null,
): StudyDesignStudy | undefined {
  if (!slug) return undefined
  return getUserStudyBySlug(userId, slug) ?? MOCK_STUDIES.find(s => s.slug === slug)
}

export function getVersionById(study: StudyDesignStudy, versionId: string): StudyDesignVersion | undefined {
  return study.versions.find(v => v.id === versionId)
}

export const STATUS_LABELS: Record<StudyStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  published: 'Published',
  exported: 'Exported',
}

export const VERSION_STATUS_LABELS: Record<VersionStatus, string> = {
  draft: 'Draft',
  validated: 'Validated',
  published: 'Published',
}

export const STATUS_BADGE: Record<StudyStatus, { variant: 'outline' | 'secondary' | 'default'; className?: string }> = {
  draft: { variant: 'outline' },
  in_review: { variant: 'secondary', className: 'bg-surface-2 text-[var(--text-secondary)] border-transparent' },
  published: { variant: 'default', className: 'bg-success-muted text-semantic-success border-transparent' },
  exported: { variant: 'default', className: 'bg-persona-stud/12 text-persona-stud border-transparent' },
}
